import type { TabState } from '../../types';
import { getFiltered } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { dedupeById, sumUniqueBy, isAged, ageSeverity, formatAge, compareAgeDesc, splitSCNames } from '../../utils/deals';
import { MetricCard } from '../ui/MetricCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { OfferingChart } from '../charts/OfferingChart';
import { useDeals } from '../../context/DealsContext';
import React, { Fragment } from 'react';
interface Props {
  state: TabState;
  searchRows?: React.ReactNode;
}
function regionBadge(region: string) {
  const styles: Record<string, {
    bg: string;
    color: string;
  }> = {
    AMER: {
      bg: '#EFF6FF',
      color: '#1D4ED8'
    },
    EMEA: {
      bg: '#F5F3FF',
      color: '#7C3AED'
    },
    JAPAC: {
      bg: '#F0FDF4',
      color: '#15803D'
    }
  };
  const s = styles[region] || {
    bg: '#F9FAFB',
    color: '#374151'
  };
  return <span className="badge" style={{
    background: s.bg,
    color: s.color,
    fontSize: 8
  }} data-id="element-295" data-mp="386zffw19fvr5">{region}</span>;
}
export function SDPanel({
  state,
  searchRows
}: Props) {
  const {
    deals
  } = useDeals();
  const all = getFiltered('sd', state, deals);
  const ip = all.filter(d => d.status === 'In Progress');
  const aged = ip.filter(d => isAged(d));
  const scs = new Set(ip.flatMap(d => splitSCNames(d.sc))).size;
  const oppGnarr = sumUniqueBy(all, d => d.opp_gnarr);

  // Closed Booked GNARR: won deals matching region/sub and close_q quarter filter
  const wonDeals = dedupeById(deals.filter(d => {
    if (d.adjusted_commit !== 'Won') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (state.q !== 'All' || state.fy !== 'All') {
      if (!d.close_q) return false;
      const [yr, qt] = d.close_q.split('-');
      if (state.fy !== 'All' && yr !== state.fy) return false;
      if (state.q !== 'All' && qt !== state.q) return false;
    }
    return true;
  }));
  const wonGnarr = wonDeals.reduce((s, d) => s + (d.opp_gnarr || 0), 0);
  const wonCount = wonDeals.length;
  const agedSorted = [...aged].sort(compareAgeDesc).slice(0, 8);
  const dealsSorted = [...ip].sort(compareAgeDesc);
  const allDealsSorted = [...all].sort(compareAgeDesc);
  return <div data-id="element-296" data-mp="2qfitsz1c4t85">
      {/* Metrics */}
      <div className="mrow" data-id="element-297" data-mp="2uft6eujmuhvz">
        <MetricCard label="In-progress requests" value={ip.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-298" data-mp="2uerktw4zqd00" /> 
        <MetricCard label="Aged >90 days" value={aged.length} sub="need immediate action" colorClass="c-red" data-id="element-299" data-mp="2zdkxxngd81zn" />
        <MetricCard label="Closed Booked GNARR" value={fmtG(wonGnarr)} sub={`${wonCount} won opportunit${wonCount === 1 ? 'y' : 'ies'}`} colorClass="c-purple" data-id="element-300" data-mp="34eik0b4hxg6f" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-301" data-mp="1a0pw3clidoew" />
      </div>

      {/* Offering chart + Aged deals */}
      <div className="row2" data-id="element-302" data-mp="1ezu6g6k65s6t">
        <div className="card" data-id="element-303" data-mp="1f15a86dm6zxx">
          <h3 data-id="element-304" data-mp="3s92epaeizopn">Requests by solution</h3>
          <OfferingChart deals={all} data-id="element-305" data-mp="3sa3wrytylbgv" />
        </div>
        <div className="card" data-id="element-306" data-mp="3niucldr6j0cd">
          <h3 data-id="element-307" data-mp="3nhj799i35kj5">Aged deals (&gt;90 days) at risk</h3>
          {agedSorted.length > 0 ? <table className="tbl" data-id="element-308" data-mp="3jg7hn3z7ozay">
              <thead data-id="element-309" data-mp="3jh8pkzkxl5og">
                <tr data-id="element-310" data-mp="3jemnhfgt7idm">
                  <th style={{
                width: '30%'
              }} data-id="element-311" data-mp="3eifoy7qqt95p">Account</th>
                  <th style={{
                width: '20%'
              }} data-id="element-312" data-mp="3eft5pauhp57n">SC</th>
                  <th style={{
                width: '12%'
              }} data-id="element-313" data-mp="f329kvpomsqc">Age</th>
                  <th style={{
                width: '18%'
              }} data-id="element-314" data-mp="f0g2syam8h96">GNARR</th>
                  <th style={{
                width: '12%'
              }} data-id="element-315" data-mp="b2icfs53zp47">Close Q</th>
                  <th style={{
                width: '8%'
              }} data-id="element-316" data-mp="61uawcj97pvq">Sub</th>
                </tr>
              </thead>
              <tbody data-id="element-317" data-mp="12ztnf0xq9dq">
                {agedSorted.map((d, rowPosition) => <Fragment key={`${d.id}-${rowPosition}`} data-id="element-318" data-mp="153v7qbd2wau" data-mpi={rowPosition}>
                  <tr key={`${d.id}`} data-id="element-319" data-mp="2qtcuu0ex9nub">
                    <td title={d.account} data-id="element-320" data-mp="2qq8lkomlo1ws">{d.account.substring(0, 22)}</td>
                    <td title={d.sc} data-id="element-321" data-mp="2lrnkyo3frg7t">{d.sc.split(' ')[0]}</td>
                    <td className="c-red" style={{
                  fontWeight: 600
                }} data-id="element-322" data-mp="dk0ki0fqwvme">{formatAge(d.age)}</td>
                    <td data-id="element-323" data-mp="ibse6ju77hgr">{fmtG(d.gnarr)}</td>
                    <td data-id="element-324" data-mp="icto2kzh9xww">{d.close_q}</td>
                    <td data-id="element-325" data-mp="4jue0yigsvwq"><span className="badge b-gray" style={{
                    fontSize: 8
                  }} data-id="element-326" data-mp="4k438jvoqz1d">{d.sub_region}</span></td>
                  </tr>
                  </Fragment>)}
              </tbody>
            </table> : <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#8892a0',
          fontSize: 11
        }} data-id="element-327" data-mp="x89lmzz9oby1">No aged deals in this filter ✓</div>}
        </div>
      </div>

      {/* In progress deals list */}
      <SectionHeader title="In-progress deals — full list" data-id="element-328" data-mp="n9afpp5vyueq" />
      <div className="tw" data-id="element-329" data-mp="s9gctkuxd947">
        <table className="tbl" data-id="element-330" data-mp="s8nb4yatp9sx">
          <thead data-id="element-331" data-mp="s7lnlucmeuub">
            <tr data-id="element-332" data-mp="sb9ioxxprpb5">
              <th style={{
              width: '11%'
            }} data-id="element-333" data-mp="380udjayavy8v">DR ID</th>
              <th style={{
              width: '22%'
            }} data-id="element-334" data-mp="37yzslwn1464s">Account</th>
              <th style={{
              width: '15%'
            }} data-id="element-335" data-mp="3d06z7sdfqz9v">SC Assigned</th>
              <th style={{
              width: '14%'
            }} data-id="element-336" data-mp="3cxksfuydcnsp">Offering</th>
              <th style={{
              width: '7%'
            }} data-id="element-337" data-mp="35fd6l5o2f429">Age</th>
              <th style={{
              width: '10%'
            }} data-id="element-338" data-mp="35i926ncfxgnj">GNARR</th>
              <th style={{
              width: '9%'
            }} data-id="element-339" data-mp="30g09ls3hcgly">Close Q</th>
              <th style={{
              width: '7%'
            }} data-id="element-340" data-mp="30je9mtoo8dq8">Region</th>
              <th style={{
              width: '5%'
            }} data-id="element-341" data-mp="3efjgyiogvnx7">Sub</th>
            </tr>
          </thead>
          <tbody data-id="element-342" data-mp="39hhzg26ovwnu">
            {searchRows ?? dealsSorted.map((d, rowPosition) => <Fragment key={`${d.id}-${rowPosition}`} data-id="element-343" data-mp="2lrnltiejq42j" data-mpi={rowPosition}>
              <tr key={d.id} data-id="element-344" data-mp="2lsfa085tczis">
                <td data-id="element-345" data-mp="2hqtyor8xgtap"><span style={{
                  fontSize: 9,
                  color: '#8892a0'
                }} data-id="element-346" data-mp="2hqu08vokt1ct">{d.id}</span></td>
                <td title={d.account} data-id="element-347" data-mp="2hoqyxfhz3lss">{d.account.substring(0, 24)}</td>
                <td title={d.sc} data-id="element-348" data-mp="2vjjo75no0gck">{d.sc.split(' ').slice(0, 2).join(' ')}</td>
                <td data-id="element-349" data-mp="2qq785jx1xhvf">
                  <span className="badge b-blue" style={{
                  fontSize: 8
                }} data-id="element-350" data-mp="2qpxijxfx5cq9">
                    {d.offering.replace('Engagement', 'Eng.').replace('Support', 'Sup.')}
                  </span>
                </td>
                <td className={ageSeverity(d.age) === 'high' ? 'c-red' : ageSeverity(d.age) === 'medium' ? 'c-gold' : ''} style={{
                fontWeight: ageSeverity(d.age) === 'high' ? 600 : 400
              }} data-id="element-351" data-mp="1y0i7j7o97juc">{formatAge(d.age)}</td>
                <td data-id="element-352" data-mp="6w6j2qit1c68">{fmtG(d.gnarr)}</td>
                <td data-id="element-353" data-mp="boznfdad2ihs">{d.close_q}</td>
                <td data-id="element-354" data-mp="bq1cwms4j7j1">{regionBadge(d.region)}</td>
                <td data-id="element-355" data-mp="go2sgxqccopr"><span className="badge b-gray" style={{
                  fontSize: 8
                }} data-id="element-356" data-mp="goufvl9sbg4y">{d.sub_region}</span></td>
              </tr>
              </Fragment>)}
          </tbody>
        </table>
      </div>

      {/* Full deals list */}
      <SectionHeader title="Closed requests — full list" data-id="element-357" data-mp="3k5ugy259qqj7" />
      <div className="tw" data-id="element-358" data-mp="3p4e5897c4eeq">
        <table className="tbl" data-id="element-359" data-mp="3p330o762f2mk">
          <thead data-id="element-360" data-mp="3p2uokhhro1cf">
            <tr data-id="element-361" data-mp="3p6ii3gn7onr5">
              <th style={{
              width: '11%'
            }} data-id="element-362" data-mp="3t5hm8z14cghj">DR ID</th>
              <th style={{
              width: '22%'
            }} data-id="element-363" data-mp="3t7ape4ciwiks">Account</th>
              <th style={{
              width: '15%'
            }} data-id="element-364" data-mp="14egfg9u2jxdh">SC Assigned</th>
              <th style={{
              width: '14%'
            }} data-id="element-365" data-mp="14fztdts95wc3">Offering</th>
              <th style={{
              width: '7%'
            }} data-id="element-366" data-mp="18eh40navtoy9">Age</th>
              <th style={{
              width: '10%'
            }} data-id="element-367" data-mp="18hd169ewo9ln">GNARR</th>
              <th style={{
              width: '9%'
            }} data-id="element-368" data-mp="2swhugkjy6tqk">Close Q</th>
              <th style={{
              width: '7%'
            }} data-id="element-369" data-mp="2ovxwxfvgpo3w">Region</th>
              <th style={{
              width: '5%'
            }} data-id="element-370" data-mp="2ox90bekzwtuh">Sub</th>
            </tr>
          </thead>
          <tbody data-id="element-371" data-mp="2k47ioytemg77">
            {searchRows ?? allDealsSorted.map((d, rowPosition) => <Fragment key={`${d.id}-${rowPosition}`} data-id="element-372" data-mp="2f6xx5u94yoon" data-mpi={rowPosition}>
              <tr key={d.id} data-id="element-373" data-mp="2f41y22ljxtym">
                <td data-id="element-374" data-mp="2a6tblptdf3rk"><span style={{
                  fontSize: 9,
                  color: '#8892a0'
                }} data-id="element-375" data-mp="2a7kzsfkn1z7t">{d.id}</span></td>
                <td title={d.account} data-id="element-376" data-mp="257ynq9gjqp0l">{d.account.substring(0, 24)}</td>
                <td title={d.sc} data-id="element-377" data-mp="256nis6bd7b7w">{d.sc.split(' ').slice(0, 2).join(' ')}</td>
                <td data-id="element-378" data-mp="2176dpdzuog39">
                  <span className="badge b-blue" style={{
                  fontSize: 8
                }} data-id="element-379" data-mp="216n20f0smnxo">
                    {d.offering.replace('Engagement', 'Eng.').replace('Support', 'Sup.')}
                  </span>
                </td>
                <td className={ageSeverity(d.age) === 'high' ? 'c-red' : ageSeverity(d.age) === 'medium' ? 'c-gold' : ''} style={{
                fontWeight: ageSeverity(d.age) === 'high' ? 600 : 400
              }} data-id="element-380" data-mp="3vibkgr68h8mt">{formatAge(d.age)}</td>
                <td data-id="element-381" data-mp="34f299kt6wn0t">{fmtG(d.gnarr)}</td>
                <td data-id="element-382" data-mp="34e0kkdj9420m">{d.close_q}</td>
                <td data-id="element-383" data-mp="34e91qhmepthm">{regionBadge(d.region)}</td>
                <td data-id="element-384" data-mp="2pqnxulhpjq8n"><span className="badge b-gray" style={{
                  fontSize: 8
                }} data-id="element-385" data-mp="2pqxnu92r5xec">{d.sub_region}</span></td>
              </tr>
              </Fragment>)}
          </tbody>
        </table>
      </div>

    </div>;
}